import axios from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {environmentLabel} from '../../install-app/components/InstallAppModal'
import {currentInstallEnvironment} from '../../install-app/core/installEnvironment'
import {isRunningInstalled} from '../../install-app/core/installPrompt'
import {rememberPushEndpoint} from './pushEndpoint'

/**
 * Web push for the panel's notifications (channel «push»): this browser's subscription, kept in
 * step with the server. The worker (public/push-worker.js) only shows what the server sends; the
 * server decides what to send by the same per-group levels as SMS and Telegram.
 */

export const PUSH_WORKER_URL = '/push-worker.js'
const BASE_URL = `${API_URL}/notifications/push`

export type PushConfig = {available: boolean; publicKey?: string | null; hint?: string | null}

export type PushDevice = {
  id: number
  deviceLabel: string
  createdAtUtc: string
  lastSuccessAtUtc?: string | null
  lastFailureAtUtc?: string | null
  isCurrent?: boolean
}

/**
 * unsupported: no push in this browser; needs-install: iPhone/iPad, where only the installed app
 * receives push (iOS 16.4+); blocked: the user refused notifications for this site; off / on: this
 * device's subscription.
 */
export type PushDeviceState = 'unsupported' | 'needs-install' | 'blocked' | 'off' | 'on'

export const getPushConfig = async () => (await axios.get<PushConfig>(`${BASE_URL}/config`)).data

export const listPushDevices = async () =>
  (await axios.get<PushDevice[]>(`${BASE_URL}/subscriptions`)).data

const saveSubscription = async (subscription: PushSubscription) => {
  const json = subscription.toJSON()
  await axios.post(`${BASE_URL}/subscriptions`, {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    deviceLabel: environmentLabel(currentInstallEnvironment()),
  })
  rememberPushEndpoint(subscription.endpoint)
}

const removeSubscription = async (endpoint: string) =>
  axios.post(`${BASE_URL}/subscriptions/remove`, {endpoint})

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

const appleDevice = () => {
  const os = currentInstallEnvironment().os
  return os === 'ios' || os === 'ipados'
}

/** The application server key as the browser wants it (base64url → bytes). */
export const urlBase64ToBytes = (value: string) => {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const raw = window.atob(padded)
  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

const workerRegistration = async () => {
  if (!pushSupported()) return undefined
  return navigator.serviceWorker.getRegistration('/')
}

const currentSubscription = async () => {
  const registration = await workerRegistration()
  return registration?.pushManager ? registration.pushManager.getSubscription() : null
}

export const pushDeviceState = async (): Promise<PushDeviceState> => {
  if (!pushSupported())
    return appleDevice() && !isRunningInstalled() ? 'needs-install' : 'unsupported'
  if (Notification.permission === 'denied') return 'blocked'
  const subscription = await currentSubscription()
  return subscription && Notification.permission === 'granted' ? 'on' : 'off'
}

/** Asks for permission (call it from a click: Safari refuses otherwise), subscribes and saves. */
export const enablePush = async (publicKey: string): Promise<PushDeviceState> => {
  if (!pushSupported()) return pushDeviceState()
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'blocked' : 'off'
  const registration = await navigator.serviceWorker.register(PUSH_WORKER_URL, {scope: '/'})
  await navigator.serviceWorker.ready
  let subscription = await registration.pushManager.getSubscription()
  if (subscription && !sameServerKey(subscription, publicKey)) {
    await subscription.unsubscribe()
    subscription = null
  }
  subscription =
    subscription ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToBytes(publicKey),
    }))
  await saveSubscription(subscription)
  return 'on'
}

/** Stops push on this device, here and on the server. */
export const disablePush = async (): Promise<PushDeviceState> => {
  const subscription = await currentSubscription()
  if (subscription) {
    await removeSubscription(subscription.endpoint)
    await subscription.unsubscribe()
    rememberPushEndpoint(null)
  }
  return pushDeviceState()
}

const sameServerKey = (subscription: PushSubscription, publicKey: string) => {
  const key = subscription.options?.applicationServerKey
  if (!key) return true
  const current = new Uint8Array(key)
  const wanted = urlBase64ToBytes(publicKey)
  return current.length === wanted.length && current.every((byte, index) => byte === wanted[index])
}

/**
 * On every start of the signed-in panel: a subscription made earlier is saved again for the user
 * now signed in (another user on the same browser, or keys the browser renewed by itself), and one
 * made for another server key is replaced. Never asks for permission.
 */
export const syncPushSubscription = async () => {
  if (!pushSupported() || Notification.permission !== 'granted') return
  const subscription = await currentSubscription()
  if (!subscription) return
  const config = await getPushConfig()
  if (!config.available || !config.publicKey) return
  if (sameServerKey(subscription, config.publicKey)) {
    await saveSubscription(subscription)
  } else {
    await enablePush(config.publicKey)
  }
}
